import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Truck, RotateCcw, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Seo } from "@/components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const { add } = useCart();
  const [size, setSize] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => (await api.get(`/products/${slug}`)).data,
  });

  if (isLoading) return <div className="px-8 py-20 text-muted-foreground">Loading…</div>;
  if (!product) return <div className="px-8 py-20" data-testid="product-not-found">Product not found. <Link to="/shop" className="underline">Back to shop</Link></div>;

  const handleAdd = () => {
    if (!size) { toast.error("Pick a size first"); return; }
    add(product, product.colors?.[0], size, 1);
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, url }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied — go share it");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name, image: product.images, description: product.description,
    brand: { "@type": "Brand", name: "The Hoodie Project" },
    offers: { "@type": "Offer", price: product.price, priceCurrency: "USD", availability: "https://schema.org/InStock" },
  };

  return (
    <div className="mx-auto max-w-[1600px]" data-testid="product-detail-page">
      <Seo title={`${product.name} — The Hoodie Project®`} description={product.description} jsonLd={jsonLd} />
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Gallery */}
        <div className="border-b border-border lg:border-b-0 lg:border-r">
          <div className="aspect-square bg-muted">
            <img src={product.images?.[imgIdx]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="flex gap-0">
            {(product.images || []).map((im, i) => (
              <button key={i} onClick={() => setImgIdx(i)} data-testid={`thumb-${i}`}
                className={`aspect-square w-24 overflow-hidden border-t border-border ${i > 0 ? "border-l" : ""} ${imgIdx === i ? "opacity-100" : "opacity-40 hover:opacity-100"}`}>
                <img src={im} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info (sticky) */}
        <div className="lg:sticky lg:top-16 lg:h-fit">
          <div className="px-5 py-10 md:px-12">
            {product.origin === "resale" && <p className="mb-3 inline-block bg-white px-2 py-1 text-[10px] font-bold tracking-widest text-black">RESALE · {product.seller_name}</p>}
            <p className="text-xs tracking-[0.3em] text-muted-foreground">{(product.colors || []).join(" / ")}</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter md:text-5xl" data-testid="product-title">{product.name}</h1>
            <p className="mt-4 font-display text-3xl font-bold" data-testid="product-price">${product.price}</p>

            <div className="mt-8">
              <p className="mb-3 text-xs tracking-[0.2em] text-muted-foreground">SELECT SIZE</p>
              <div className="grid grid-cols-4 gap-0 border border-border">
                {(product.sizes || []).map((s, i) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`py-4 text-sm tracking-widest transition-colors ${i > 0 ? "border-l border-border" : ""} ${size === s ? "bg-white text-black" : "hover:bg-muted"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={handleAdd} data-testid="add-to-cart-btn"
              className="mt-8 flex w-full items-center justify-center gap-3 bg-white py-5 text-xs font-bold tracking-[0.3em] text-black transition-colors hover:bg-neutral-300">
              <Check className="h-4 w-4" /> ADD TO BAG
            </button>
            <button onClick={share} data-testid="share-btn"
              className="mt-3 flex w-full items-center justify-center gap-3 border border-border py-4 text-xs font-bold tracking-[0.2em] transition-colors hover:bg-white hover:text-black">
              <Share2 className="h-4 w-4" /> SHARE
            </button>

            <p className="mt-8 text-sm leading-relaxed text-muted-foreground">{product.description}</p>

            <div className="mt-8 space-y-3 border-t border-border pt-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-3"><Truck className="h-4 w-4" /> Fast US shipping with tracking at checkout.</div>
              <div className="flex items-center gap-3"><RotateCcw className="h-4 w-4" /> Taxes calculated automatically at checkout.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
